import { useContext } from "react"
import { useLocation } from "@reach/router"
import { LoaderContext } from "components/Providers"
import { CompanyNode } from "types/companies"
import { ScrollTrigger } from "gsap/all"

export const isBrowser = () => typeof window !== "undefined"

export const isIOS = () => {
  if (isBrowser()) {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
  } else {
    return false
  }
}

export const generateGrid = (arr: CompanyNode[]) => {
  let x = 0
  let y = 0
  const grid: { x: number; y: number; tier?: number }[] = []
  // center square
  grid.push({ x, y })

  let tiers = 0
  for (let j = arr.length - 1; j > 0; tiers++) {
    j -= 6 * tiers
  }

  const horizontalSpacing = 280 + 40
  const verticalSpacing = 400 + 40

  for (let i = 1; i < tiers; i++) {
    // move up 1
    y += verticalSpacing
    grid.push({ x: x, y: y, tier: i })

    // move down & right i times
    for (let j = 0; j < i; j++) {
      if (grid.length !== arr.length) {
        x += horizontalSpacing
        y -= verticalSpacing / 2
        grid.push({ x: x, y: y, tier: i })
      }
    }

    // move down i times
    for (let j = 0; j < i; j++) {
      if (grid.length !== arr.length) {
        y -= verticalSpacing
        grid.push({ x: x, y: y, tier: i })
      }
    }

    // move left & down i times
    for (let j = 0; j < i; j++) {
      if (grid.length !== arr.length) {
        x -= horizontalSpacing
        y -= verticalSpacing / 2
        grid.push({ x: x, y: y, tier: i })
      }
    }

    // move left & up i times
    for (let j = 0; j < i; j++) {
      if (grid.length !== arr.length) {
        x -= horizontalSpacing
        y += verticalSpacing / 2
        grid.push({ x: x, y: y, tier: i })
      }
    }

    // move up i times
    for (let j = 0; j < i; j++) {
      if (grid.length !== arr.length) {
        y += verticalSpacing
        grid.push({ x: x, y: y, tier: i })
      }
    }

    // move right & up i - 1 times
    for (let j = 0; j < i - 1; j++) {
      if (grid.length !== arr.length) {
        x += horizontalSpacing
        y += verticalSpacing / 2
        grid.push({ x: x, y: y, tier: i })
      }
    }

    // move x and y to first position in tier
    x += horizontalSpacing
    y += verticalSpacing / 2
  }

  const verticalBound = tiers * verticalSpacing + verticalSpacing
  const horizontalBound = tiers * horizontalSpacing + horizontalSpacing

  const returnVals = {
    grid,
    verticalBound,
    horizontalBound,
    tiers,
  }

  return returnVals
}

export const useInternalLink = (route: string) => {
  const location = useLocation()
  const setLoad = useContext(LoaderContext).setLoad

  if (route) {
    location.pathname !== route && setLoad(route)
  }
}

export const addDebouncedEventListener = (
  element: Window | HTMLElement,
  event: string,
  callback: Function,
  delay: number
) => {
  let timeout: NodeJS.Timeout
  const debouncedCallback = (...args: any[]) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => callback(...args), delay)
  }
  element.addEventListener(event, debouncedCallback)
}

export const vwToPx = (vw: number) => {
  if (isBrowser()) {
    const px = vw * (window.innerWidth / 100)
    return px
  } else {
    return 0
  }
}

//shuffle an array generically
export const shuffle = <T>(array: T[]) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[array[i], array[j]] = [array[j], array[i]]
  }
  return array
}

export const fixBrokenScroll = () => {
  if (!isBrowser()) return

  window.locomotiveScroll?.stop()
  window.locomotiveScroll?.scrollTo("top", {
    duration: 0.0001,
  })
  window.locomotiveScroll?.stop()
  ScrollTrigger.refresh()
}

export const sleep = (ms: number) => {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export const documentReady = async () => {
  //adding just a bit of artificial delay makes everything less likely to break
  await sleep(100)
  return new Promise(resolve => {
    // see if DOM is already available
    if (
      document.readyState === "complete" ||
      document.readyState === "interactive"
    ) {
      // call on next available tick
      resolve(null)
    } else {
      document.addEventListener("DOMContentLoaded", resolve)
    }
  })
}

export const lerp = (x: number, y: number, t: number) => {
  return (1 - t) * x + t * y
}
